import { useEffect, useRef, useState } from 'react';
import { LatLng } from 'leaflet';
import { ActiveNodePosition } from '@features/components/LocationTracking/types';
import { shortestAngleDelta } from '@features/components/LocationTracking/utils/animation.utils';
import { BaseLocationMarker } from '@features/components/LocationTracking/components/BaseLocationMarker/BaseLocationMarker';
import { DeviceNodeTypeEnum } from '@/api/apis/composer';

const INTERPOLATION_DURATION_MS = 1500;

type PositionMarkerProps = {
  activePosition: ActiveNodePosition;
};

export const PositionMarker = ({ activePosition }: PositionMarkerProps) => {
  const { location, heading, nodeType, isActive } = activePosition;
  const targetLat = location.lat;
  const targetLng = location.lng;

  const [displayPosition, setDisplayPosition] = useState<LatLng>(location);
  const [displayHeading, setDisplayHeading] = useState(heading);
  const [displayNodeType, setDisplayNodeType] = useState<DeviceNodeTypeEnum>(nodeType);

  const prevPositionRef = useRef(location);
  const prevHeadingRef = useRef(heading);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const startLat = prevPositionRef.current.lat;
    const startLng = prevPositionRef.current.lng;
    const startHeading = prevHeadingRef.current;

    const isNewPosition = startLat !== targetLat || startLng !== targetLng;

    if (!isNewPosition) {
      setDisplayHeading(heading);
      setDisplayNodeType(nodeType);
      prevHeadingRef.current = heading;
      return () => {};
    }

    if (animationRef.current !== null) {
      cancelAnimationFrame(animationRef.current);
    }

    let startTime: number | null = null;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / INTERPOLATION_DURATION_MS, 1);
      const eased = 1 - (1 - progress) ** 3;

      const lat = startLat + (targetLat - startLat) * eased;
      const lng = startLng + (targetLng - startLng) * eased;
      const h = startHeading + shortestAngleDelta(startHeading, heading) * eased;

      setDisplayPosition(new LatLng(lat, lng));
      setDisplayHeading(h);
      setDisplayNodeType(nodeType);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        prevPositionRef.current = location;
        prevHeadingRef.current = heading;
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [targetLat, targetLng, heading, nodeType, location]);

  return (
    <BaseLocationMarker
      position={displayPosition}
      nodeType={displayNodeType}
      heading={displayHeading}
      pulsating={isActive}
    />
  );
};
